import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import ChartJs from 'chart.js/auto';
import classNames from 'classnames/bind';
import React, { useState, useRef, useMemo } from 'react';

import styles from './Geomagnetic.module.scss';
import Term from './Term';

import Bar from '@/components/ui/Bar';
import API from '@/helpers/API';
import dayjs from '@/libs/dayjs';

const cx = classNames.bind(styles);

// const DUMMY = {
//   Error: false,
//   ErrorCode: 'NOERR',
//   Kindex: {
//     Time: '2025-10-01 21:00:00',
//     CurrentP: 3,
//     CurrentK: 2,
//     Max24P: 4,
//     Max24K: 5,
//     Recent: [
//       { Time: '2025-10-01 21:00:00', Kp: 2, Kk: 1 },
//       { Time: '2025-10-01 18:00:00', Kp: 2, Kk: 1 },
//       { Time: '2025-10-01 15:00:00', Kp: 3, Kk: 2 },
//       { Time: '2025-10-01 12:00:00', Kp: 3, Kk: 2 },
//       { Time: '2025-10-01 09:00:00', Kp: 4, Kk: 3 },
//       { Time: '2025-10-01 06:00:00', Kp: 2, Kk: 1 },
//       { Time: '2025-10-01 03:00:00', Kp: 3, Kk: 2 },
//       { Time: '2025-10-01 00:00:00', Kp: 9, Kk: 1 },
//     ],
//   },
// };

const Geomagnetic = () => {
  const [data, setData] = useState();
  const [isOpenChart, setOpenChart] = useState(false);
  const termRef = useRef();
  const chartRef = useRef();
  const chart = useRef();

  useMountEffect(() => {
    API.get('/proxy/geomagnetic').then((result) => {
      if (result.success) {
        setData(result.data);
      }

      //! 더미 데이터 임시 활용
      // setData(DUMMY);
    });
  });

  useUpdateEffect(() => {
    drawChart();
  }, [data]);

  useUpdateEffect(() => {
    if (isOpenChart) {
      document.addEventListener('mousedown', unfocusPanel);
    }

    return () => {
      document.removeEventListener('mousedown', unfocusPanel);
    };
  }, [isOpenChart]);

  const unfocusPanel = (e) => {
    if (termRef.current.contains(e.target)) return;

    setOpenChart(false);
  };

  const toggleChart = (e) => {
    // Panel 클릭된 경우 이벤트 방지
    const closest = e.target.closest('[data-panel]');
    if (!closest) {
      setOpenChart(!isOpenChart);
    }
  };

  const drawChart = () => {
    const context = chartRef.current.getContext('2d');
    const options = {
      type: 'line',
      data: getChartData(),
      options: {
        layout: {
          padding: {
            top: 12,
            right: 12,
            bottom: 0,
            left: 8,
          },
        },
        maintainAspectRatio: false,
        scales: {
          xAxis: {
            grid: { display: false },
            ticks: { font: { size: 10 } },
          },
          yAxis: {
            min: 0,
            max: 10,
            grid: { color: 'rgba(255, 255, 255, 0.1)' },
            ticks: { font: { size: 10 } },
          },
        },
        plugins: { legend: false },
      },
    };

    chart.current = new ChartJs(context, options);
  };

  const getChartData = () => {
    const reversed = [...data.Recent].reverse();

    return {
      labels: reversed.map((data, index) => {
        if (index === reversed.length - 1) {
          return dayjs(data.Time).format('HH');
        }
        const lastTime = dayjs(reversed[reversed.length - 1].Time);
        const hoursAgo = dayjs(data.Time).diff(lastTime, 'hour');
        return `${hoursAgo}h`;
      }),
      datasets: [
        {
          label: 'Kp Index',
          data: reversed.map(({ Kp }) => Kp),
          borderColor: '#ff6b6b',
          borderWidth: 1,
          pointRadius: 1,
          pointBackgroundColor: '#ff6b6b',
        },
        {
          label: 'Kk Index',
          data: reversed.map(({ Kk }) => Kk),
          borderColor: '#4ecdc4',
          borderWidth: 1,
          pointRadius: 1,
          pointBackgroundColor: '#4ecdc4',
        },
      ],
    };
  };

  return (
    <div className={cx('container')} data-hide-priority={1}>
      <Term
        ref={termRef}
        label="Geomagnetic"
        value={data && `Kp ${data.CurrentP} / Kk ${data.CurrentK}`}
        onClick={data ? toggleChart : undefined}>
        <div data-panel className={cx('panel', { show: isOpenChart })}>
          <div className={cx('header')}>
            <div className={cx('title')}>Geomagnetic</div>
            <div className={cx('legends')}>
              <div className={cx('legend')}>
                <div className={cx('color', 'kp')}></div>
                <div className={cx('label')}>Kp Index</div>
              </div>
              <div className={cx('legend')}>
                <div className={cx('color', 'kk')}></div>
                <div className={cx('label')}>Kk Index</div>
              </div>
            </div>
          </div>
          <div className={cx('body')}>
            <canvas ref={chartRef}></canvas>
          </div>
        </div>
      </Term>
      <Bar size={28} />
    </div>
  );
};

export default Geomagnetic;
